import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { forwardRef, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { useIsMobile } from "@/hooks/use-breakpoint";

const button = cva(
  "inline-flex items-center justify-center gap-1.5 font-body whitespace-nowrap transition-all duration-150 disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:brightness-100 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/[0.08] hover:-translate-y-px active:scale-[0.97] active:translate-y-0 hover:brightness-[0.92]",
  {
    variants: {
      variant: {
        primary:    "bg-primary text-primary-foreground rounded-md font-medium",
        accent:     "bg-foreground text-background rounded-md font-medium",
        secondary:  "bg-foreground/[0.06] text-foreground rounded-md font-medium hover:bg-foreground/[0.1]",
        outline:    "border-[0.5px] border-foreground/20 text-foreground rounded-md font-medium bg-transparent hover:bg-foreground/[0.04]",
        ghost:      "text-foreground/60 rounded-md font-medium bg-transparent hover:bg-foreground/[0.05]",
        destructive:"bg-destructive/10 text-destructive border-[0.5px] border-destructive/20 rounded-md font-medium hover:bg-destructive/15",
        pill:       "bg-primary text-primary-foreground rounded-full font-medium",
        link:       "text-primary p-0 h-auto bg-transparent hover:underline underline-offset-2 hover:translate-y-0 hover:brightness-100 active:scale-100",
        white:      "bg-card text-foreground rounded-md font-medium shadow-card",
        orange:     "bg-brand-orange text-white rounded-md font-medium",
      },
      size: {
        sm: "px-3.5 py-1.5 text-xs",
        md: "px-5 py-2.5 text-[13px]",
        lg: "px-7 py-3 text-[15px] font-light",
      },
      iconOnly: {
        true: "!p-0 rounded-full",
        false: "",
      },
    },
    compoundVariants: [
      { iconOnly: true, size: "sm", className: "w-8 h-8" },
      { iconOnly: true, size: "md", className: "w-10 h-10" },
      { iconOnly: true, size: "lg", className: "w-12 h-12" },
    ],
    defaultVariants: { variant: "primary", size: "md", iconOnly: false },
  }
);

export type SplitItem = {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  divider?: boolean;
};

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">,
    VariantProps<typeof button> {
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  children?: ReactNode;
  splitItems?: SplitItem[];
  splitAlign?: "left" | "right";
}

const DARK_VARIANTS = new Set(["primary", "accent", "pill", "orange", "destructive"]);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, iconOnly, loading, iconLeft, iconRight, children, disabled, splitItems, splitAlign = "right", onClick, ...props },
    ref
  ) => {
    // Split mode -------------------------------------------------------------
    if (splitItems && splitItems.length > 0) {
      return (
        <SplitButton
          variant={variant}
          size={size}
          loading={loading}
          disabled={disabled}
          iconLeft={iconLeft}
          iconRight={iconRight}
          onClick={onClick}
          items={splitItems}
          align={splitAlign}
          className={className}
          buttonProps={props}
          forwardedRef={ref}
        >
          {children}
        </SplitButton>
      );
    }

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        onClick={onClick}
        className={cn(button({ variant, size, iconOnly }), loading && "cursor-wait opacity-70", className)}
        {...props}
      >
        {loading ? (
          <span className="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-ds-spin" />
        ) : (
          <>
            {iconLeft && <span className="inline-flex shrink-0 [&>svg]:w-[15px] [&>svg]:h-[15px]">{iconLeft}</span>}
            {children}
            {iconRight && <span className="inline-flex shrink-0 [&>svg]:w-[15px] [&>svg]:h-[15px]">{iconRight}</span>}
          </>
        )}
      </button>
    );
  }
);
Button.displayName = "Button";

/* ───────────────────────── SplitButton ───────────────────────── */

const ARROW_W = { sm: "w-7", md: "w-9", lg: "w-11" } as const;

function SplitButton({
  variant = "primary",
  size = "md",
  loading,
  disabled,
  iconLeft,
  iconRight,
  onClick,
  items,
  align,
  className,
  children,
  buttonProps,
  forwardedRef,
}: {
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  loading?: boolean;
  disabled?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
  items: SplitItem[];
  align: "left" | "right";
  className?: string;
  children?: ReactNode;
  buttonProps: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "onClick">;
  forwardedRef: React.Ref<HTMLButtonElement>;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();
  const isDark = DARK_VARIANTS.has(variant ?? "primary");

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Base classes per half — strip the radius on the inner edges
  const baseLeft = cn(
    button({ variant, size, iconOnly: false }),
    "!rounded-r-none hover:translate-y-0 active:translate-y-0 active:scale-100",
    loading && "cursor-wait opacity-70"
  );
  const baseRight = cn(
    button({ variant, size, iconOnly: false }),
    "!rounded-l-none hover:translate-y-0 active:translate-y-0 active:scale-100 !px-0 !gap-0",
    ARROW_W[size ?? "md"]
  );

  return (
    <div
      ref={wrapperRef}
      className={cn(
        "relative inline-flex transition-transform duration-150",
        !disabled && "hover:-translate-y-px active:scale-[0.97]",
        className
      )}
    >
      {/* Main action */}
      <button
        ref={forwardedRef}
        disabled={disabled || loading}
        onClick={onClick}
        className={baseLeft}
        {...buttonProps}
      >
        {loading ? (
          <span className="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-ds-spin" />
        ) : (
          <>
            {iconLeft && <span className="inline-flex shrink-0 [&>svg]:w-[15px] [&>svg]:h-[15px]">{iconLeft}</span>}
            {children}
            {iconRight && <span className="inline-flex shrink-0 [&>svg]:w-[15px] [&>svg]:h-[15px]">{iconRight}</span>}
          </>
        )}
      </button>

      {/* Divider */}
      <span
        aria-hidden
        style={{
          width: "0.5px",
          alignSelf: "stretch",
          background: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.12)",
          zIndex: 1,
        }}
      />

      {/* Arrow toggle */}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More options"
        disabled={disabled || loading}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={baseRight}
      >
        <ChevronDown
          size={14}
          strokeWidth={2}
          className={cn("transition-transform duration-200", open && "rotate-180")}
        />
      </button>

      {/* Floating menu */}
      {open && <SplitMenu items={items} align={align} mobileSheet={isMobile} onClose={() => setOpen(false)} />}
    </div>
  );
}

function SplitMenu({
  items,
  align,
  mobileSheet,
  onClose,
}: {
  items: SplitItem[];
  align: "left" | "right";
  mobileSheet: boolean;
  onClose: () => void;
}) {
  const sheetStyle: React.CSSProperties = mobileSheet
    ? { position: "fixed", left: 0, right: 0, bottom: 0, borderRadius: "16px 16px 0 0", padding: "10px 8px 18px" }
    : { position: "absolute", top: "calc(100% + 6px)", [align]: 0, padding: 5, borderRadius: 10 } as React.CSSProperties;

  const menu = (
    <div
      role="menu"
      style={{
        background: "hsl(var(--card))",
        border: "0.5px solid rgba(0,0,0,0.12)",
        boxShadow:
          "rgba(0,0,0,0.03) 0px 0px 0px 1px, rgba(0,0,0,0.06) 0px 4px 12px, rgba(0,0,0,0.12) 0px 12px 32px",
        minWidth: 172,
        zIndex: 50,
        animation: mobileSheet ? "ds-slide-up 200ms ease" : "ds-fade-in 150ms ease",
        ...sheetStyle,
      }}
    >
      {items.map((it, i) =>
        it.divider ? (
          <div key={`d-${i}`} style={{ height: "0.5px", background: "rgba(0,0,0,0.07)", margin: "4px 5px" }} />
        ) : (
          <SplitMenuRow key={it.id} item={it} onClose={onClose} />
        )
      )}
    </div>
  );

  if (mobileSheet) {
    return (
      <>
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 49,
            animation: "ds-fade-in 150ms ease",
          }}
        />
        {menu}
      </>
    );
  }
  return menu;
}

function SplitMenuRow({ item, onClose }: { item: SplitItem; onClose: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      role="menuitem"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => {
        e.stopPropagation();
        item.onClick?.();
        onClose();
      }}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "8px 10px",
        borderRadius: 7,
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 300,
        color: item.danger ? "#9B0000" : "hsl(var(--foreground))",
        background: hover ? (item.danger ? "rgba(199,4,4,0.05)" : "rgba(0,0,0,0.04)") : "transparent",
        border: "none",
        textAlign: "left",
        transition: "background 120ms ease",
      }}
    >
      {item.icon && (
        <span
          className="inline-flex shrink-0 [&>svg]:w-[14px] [&>svg]:h-[14px]"
          style={item.danger ? { color: "#C70404" } : undefined}
        >
          {item.icon}
        </span>
      )}
      <span>{item.label}</span>
    </button>
  );
}
